// src/components/page-content-v4/modules/CollapsibleSection.tsx
import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  icon?: React.ReactNode;
  badge?: string | number;
  forceState?: { expanded: boolean; key: number }; // External control with key to force updates
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultExpanded = false,
  icon,
  badge,
  forceState,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const lastForceKey = useRef<number | undefined>(undefined);

  // Sync with external control when key changes (ensures re-trigger even with same value)
  useEffect(() => {
    if (forceState && forceState.key !== lastForceKey.current) {
      setIsExpanded(forceState.expanded);
      lastForceKey.current = forceState.key;
    }
  }, [forceState]);

  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-gray-50/50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}>
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </div>
          {icon && <div className="text-gray-500">{icon}</div>}
          <h3 className="text-sm font-medium text-gray-800">{title}</h3>
          {badge !== undefined && (
            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
              {badge}
            </span>
          )}
        </div>
      </button>
      <div
        className={`grid transition-[grid-template-rows,opacity] duration-200 ease-in-out ${
          isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 pt-2 border-t border-gray-50">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
